import { OPERATORS } from '../../constants';
import { BaseOperatorStrategy } from './base.operator-strategy';
import type {
  SelectionBlockerArgs,
  SelectionBlockerResult,
  UnitCostCalculationArgs,
} from '../operator-strategy';

/**
 * VesselFinder — AIS credit-wallet operator. Same pricing model as Voyage
 * Radar's strategy (unitcost × apiCredits, no holdback/cloud/ONA factors) —
 * read that file's header for the reasoning this one does not repeat.
 *
 * **Global coverage, unlike Voyage Radar** — VesselFinder tasks the entire
 * globe rather than a drawn AOI/POI, so no target-shape blocker applies here.
 * `getSelectionWarning` below surfaces a non-blocking info popup instead,
 * telling the user their AOI/POI selection has no effect on this operator.
 *
 * Recurrency is unsupported (frontend plan Q8): `disallowRepeatFrequencyInput`
 * on the operator doc, and `noOfRepeats` stays out of the cost formula on both
 * sides.
 */
export class VesselFinderOperatorStrategy extends BaseOperatorStrategy {
  readonly operatorKey = OPERATORS.VESSELFINDER;

  /** Set unitcost from the sensor row's latency tier and selectedCost to unitcost × apiCredits. */
  override calculateUnitCost({ sensorRow }: UnitCostCalculationArgs): void {
    if (!sensorRow) return;

    const unitPrice = Number(sensorRow.unitcost ?? sensorRow.latency?.[0]?.unitcost ?? 0);
    // `?? 1`, not `?? 0`: before the popup runs a zero would zero out
    // latency[0].cost and the tier dropdown would render "Not Offered".
    const credits = Number(sensorRow.apiCredits ?? 1);

    if (Array.isArray(sensorRow.latency) && sensorRow.latency[0]) {
      sensorRow.latency[0].cost = unitPrice;
      sensorRow.latency[0].unitcost = unitPrice;
    }
    sensorRow.unitcost = unitPrice;
    sensorRow.selectedCost = unitPrice * credits;
  }

  protected computeBaseCost(sensorRow: any, latencyIndx: number): number | null {
    if (!sensorRow || !Array.isArray(sensorRow.latency)) return null;
    const unitcost = Number(sensorRow.latency[latencyIndx]?.unitcost ?? 0);
    const credits = Number(sensorRow.apiCredits ?? 1);
    return unitcost * credits;
  }

  /** Blocks non-period frequency options (Specific Day, Specific Date, Recurrent) and multi-sensor tasking. */
  getSelectionBlocker(args: SelectionBlockerArgs): SelectionBlockerResult | null {
    const { sensorRow, taskingContext } = args;
    const selectedOption = taskingContext?.selectedOption;
    const sensorName = `${sensorRow?.displayName} ${sensorRow?.sensor}`;

    if (
      selectedOption === 0 ||
      selectedOption === 1 ||
      selectedOption === 3 ||
      taskingContext?.isRecurrent
    ) {
      return {
        code: 'VESSELFINDER_PERIOD_ONLY',
        message:
          `${sensorName} accepts only orders with a period collection at this time. Recurrent, Specific Date and Specific Day orders are a future capability. Please choose an alternative provider.`,
      };
    }

    if (
      taskingContext?.taskStrategy === 'Tip-and-Cue' ||
      taskingContext?.hasOtherSensorsSelected
    ) {
      return {
        code: 'VESSELFINDER_MULTI_SENSOR_UNSUPPORTED',
        message:
          `Multi-sensor tasking is not supported for ${sensorName}. Please task ${sensorName} as a single sensor, or choose an alternative provider.`,
      };
    }

    return null;
  }

  /**
   * Coverage warning is displayed upon confirming credit purchase, not upfront on selection.
   */
  getSelectionWarning(_args?: SelectionBlockerArgs): SelectionBlockerResult | null {
    return null;
  }


  /**
   * Stated here because the operator document has nothing to state it with —
   * same reasoning as Voyage Radar's identical override: `cancel_policy: []`
   * on the doc (auto-approved in cancel.route.js) renders an empty tooltip
   * otherwise.
   */
  getCancellationPolicyText(): string {
    return 'No cancelation is permitted once credits are purchased';
  }
}
